/**
 * SkillDelivery Platform
 * InviteWelcomeClassic -- the CURRENT invite welcome page.
 *
 * R14 Round 1: this file was renamed from InviteWelcome.jsx so a
 * sibling InviteWelcome.light.jsx can live beside it. Behaviourally
 * UNCHANGED from the pre-R14 version. The top-level InviteWelcome.jsx
 * is now a thin dispatcher that reads `data.page.layout` from the
 * server (resolved by INVITE_PAGE_LAYOUT) and mounts either this
 * file or the Light sibling.
 *
 * Default INVITE_PAGE_LAYOUT=classic -> this file renders. Nothing
 * changes for a contact until the Render env is flipped.
 *
 * Never shows contact phone / email / notes. Only owner's public card.
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Mail, ExternalLink, Share2, MessageCircle } from 'lucide-react';
import PreviewShell from '../components/invite/PreviewShell';
import InitialsBadge from '../components/invite/InitialsBadge';
import RoadmapStages from '../components/invite/RoadmapStages';
import CardGrid from '../components/invite/CardGrid';
import Turnstile from '../components/security/Turnstile';

export const API_BASE = (() => {
  const raw = process.env.REACT_APP_API_URL || 'https://skilldelivery-platform.onrender.com/api';
  return raw.endsWith('/api') ? raw : `${raw.replace(/\/$/, '')}/api`;
})();
export const APP_BASE = typeof window !== 'undefined' ? window.location.origin : '';
export const PENDING_INVITE_KEY = 'pendingInvite';

// Interactive-element colour. Only the gradient banner, sidebar
// customer-block accent, and the current roadmap-stage card track the
// customer brand hue. Everything the user can click stays SkillDelivery
// cyan so the brand voice is consistent.
export const SKD_CYAN      = '#06b6d4';
export const SKD_CYAN_DARK = '#0e7490';
export const SKD_CTA_GRADIENT = 'linear-gradient(135deg, #06b6d4, #0369a1)';

export default function InviteWelcomeClassic({ initialState, initialContent } = {}) {
  const { token } = useParams();
  // R14 Round 2 fix 1 -- accept initial state/content from the
  // dispatcher so the GET /public/invites/:token (which stamps
  // viewed_at + emits the "pitch viewed" audit on first open) runs
  // ONCE per page load. When mounted stand-alone (no props), fall
  // back to the self-contained fetch so a direct mount in a test
  // harness still works -- behaviour is identical to pre-R14.
  const [state, setState] = useState(() => initialState || { loading: true });
  const [content, setContent] = useState(() => initialContent || null);
  const skipFetch = !!initialState;

  useEffect(() => {
    // Dispatcher-provided state/content wins. Later refreshes of
    // `initialContent` (e.g. the content fetch resolves AFTER the
    // main page JSON) propagate here.
    if (initialState) setState(initialState);
  }, [initialState]);
  useEffect(() => {
    if (initialContent) setContent(initialContent);
  }, [initialContent]);

  useEffect(() => {
    if (skipFetch) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/public/invites/${encodeURIComponent(token)}`, { cache: 'no-store' });
        const body = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.status === 410) {
          setState({ loading: false, expired: true, owner: body.owner || null });
          return;
        }
        if (res.status === 404 || !res.ok || !body.success) {
          setState({ loading: false, notFound: true });
          return;
        }
        setState({ loading: false, page: body.data.page, meta: body.data.meta });
      } catch (_) {
        if (!cancelled) setState({ loading: false, notFound: true });
      }
    })();
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/public/invites/${encodeURIComponent(token)}/content`, {
          headers: { Accept: 'application/json' }
        });
        if (res.status === 204) return;
        const body = await res.json().catch(() => ({}));
        if (!cancelled && body && body.success) setContent(body.data || null);
      } catch (_) { /* best-effort */ }
    })();
    return () => { cancelled = true; };
  }, [token, skipFetch]);

  const recordClick = useCallback(async (itemType, itemId) => {
    try {
      await fetch(`${API_BASE}/public/invites/${encodeURIComponent(token)}/click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_type: itemType, item_id: itemId || null })
      });
    } catch (_) { /* best-effort */ }
  }, [token]);

  // R13 Step 8 -- hooks must run unconditionally. Early returns for
  // loading / notFound / expired come LATER so useInviteFeedback +
  // useEffect fire on every render in the same order (react-hooks rule).
  const _page = state && state.page;
  const _org = (_page && _page.contact && (_page.contact.organisation || _page.contact.first_name)) || 'your team';
  const _ownerFirst = (_page && _page.owner && _page.owner.full_name
    ? _page.owner.full_name.split(/\s+/)[0]
    : null) || 'the sender';
  const _ownerFullName = (_page && _page.owner && _page.owner.full_name) || _ownerFirst;
  const _roadmap = _page && _page.sections && _page.sections.roadmap;
  const _isPreview = !!(_page && _page.preview === true);

  const feedback = useInviteFeedback({
    token, isPreview: _isPreview, content, ownerFirst: _ownerFirst, roadmap: _roadmap
  });

  const topBannerText = (content && content.welcome && content.welcome.top_banner)
    || `You are previewing ${_org}'s workspace, prepared by SkillDelivery AI · shared by ${_ownerFullName}`;

  useEffect(() => {
    // Browser tab title tracks the banner wording. Only applied once
    // the main page JSON has resolved -- loading / 404 / expired keep
    // whatever title the shell started with.
    if (typeof document !== 'undefined' && _page) {
      document.title = topBannerText;
    }
  }, [topBannerText, _page]);

  if (state.loading) {
    return <PreviewShell brand={{}} organisation=""><Loading /></PreviewShell>;
  }

  if (state.notFound) {
    return <PreviewShell brand={{}} organisation=""><NotFound /></PreviewShell>;
  }

  if (state.expired) {
    return (
      <PreviewShell brand={{}} organisation="">
        <Expired owner={state.owner} />
      </PreviewShell>
    );
  }

  const { page, meta } = state;
  const brand = page.brand || {};
  const brandColor = brand.color || '#0891b2';
  const org = _org;
  const ownerFirst = _ownerFirst;
  const insights = page.sections?.insights || [];
  const research = page.sections?.research || [];
  const roadmap = _roadmap;
  // R13 Step 8 (task 2) -- the old sections.services / .ai / .grow /
  // .resources arrays are no longer rendered. The ONE catalog view
  // is `content.offerings` (DB-driven), displayed via
  // WhatWeCanHelpWithSection with status pill + pilot button.

  // Preview mode (owner-only link via POST /pitches/:id/preview-link):
  // CTAs are disabled so an owner proofing their page can't accidentally
  // start their own account from inside the preview.
  const isPreview = _isPreview;

  const topStrip = (
    <>
      <span>{topBannerText}</span>
      {isPreview ? (
        <span
          aria-disabled="true"
          title="Disabled in preview"
          style={{
            background: '#e2e8f0', color: '#64748b', textDecoration: 'none',
            padding: '6px 14px', borderRadius: 6, fontWeight: 600, fontSize: 13,
            display: 'inline-flex', alignItems: 'center', gap: 6,
            cursor: 'not-allowed'
          }}
        >
          Disabled in preview
        </span>
      ) : (
        <a
          href={`${APP_BASE}/register`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            try {
              localStorage.setItem(PENDING_INVITE_KEY, JSON.stringify({
                token, expiresAt: meta?.expires_at || null
              }));
            } catch (_) {}
            recordClick('cta', null);
          }}
          style={{
            background: SKD_CYAN, color: 'white', textDecoration: 'none',
            padding: '6px 14px', borderRadius: 6, fontWeight: 600, fontSize: 13,
            display: 'inline-flex', alignItems: 'center', gap: 6
          }}
        >
          Make this my workspace <ExternalLink size={13} />
        </a>
      )}
    </>
  );

  return (
    <PreviewShell brand={brand} organisation={org} ownerName={page.owner?.full_name} topStrip={topStrip}>
      {page.preview && (
        <div style={{
          marginBottom: 20,
          padding: '10px 14px',
          borderRadius: 10,
          background: '#fef3c7',
          border: '1px solid #fcd34d',
          color: '#92400e',
          fontSize: 13,
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }}>
          <span style={{
            background: '#92400e', color: '#fef3c7',
            padding: '2px 8px', borderRadius: 999, fontSize: 11, letterSpacing: '0.04em'
          }}>Preview</span>
          Owner preview — nothing on this page is live. Links, tracking and sign-up are disabled.
        </div>
      )}
      <section id="overview" style={{ scrollMarginTop: 20 }}>
        <GradientBanner
          brandColor={brandColor}
          organisation={org}
          firstName={page.contact?.first_name}
          personal={page.sections?.personal}
          entityType={page.contact?.represents}
          domain={page.contact?.domain}
          industryLabel={page.contact?.industry_label || null}
        />
        <OwnerCard
          owner={page.owner}
          meetingLabel={page.event_label}
          shareUrl={typeof window !== 'undefined' ? window.location.href : ''}
        />
      </section>

      {(() => {
        // Release 12 Stage 2 -- the roadmap moved to display.steps on
        // the new platform-engine shape. Fall back to the top-level
        // .steps for one release so sent-but-not-yet-opened invites
        // from before the migration still render. R13 drops the
        // fallback.
        const stepsSource = (roadmap && roadmap.display && Array.isArray(roadmap.display.steps))
          ? roadmap.display.steps
          : (roadmap && Array.isArray(roadmap.steps) ? roadmap.steps : null);
        const haveSteps = Array.isArray(stepsSource) && stepsSource.length > 0;
        const roadmapStatus = page.roadmap_status || null;

        // Entity-aware heading. Platform-engine entity_type (preferred)
        // or the contact's `represents` (legacy fallback).
        const entityType = (roadmap && roadmap.entity_type) || null;
        const represents = page.contact && page.contact.represents;
        let heading = 'Your roadmap';
        if (entityType === 'organization' || represents === 'company') {
          heading = "Your team's roadmap";
        } else if (entityType === 'university' || entityType === 'institute'
                   || represents === 'university_college' || represents === 'institute') {
          heading = "Your students' roadmap";
        }

        if (haveSteps) {
          return (
            <section id="roadmap" style={{ marginTop: 36, scrollMarginTop: 20 }}>
              <SectionHeading title={heading} />
              <RoadmapStages steps={stepsSource} brandColor={brandColor} />
            </section>
          );
        }
        if (roadmapStatus === 'pending') {
          return (
            <section id="roadmap" style={{ marginTop: 36, scrollMarginTop: 20 }}>
              <SectionHeading title={heading} />
              <p style={{ color: '#64748b', margin: 0 }}>
                Your roadmap is still being prepared. Refresh in a few seconds to see it.
              </p>
            </section>
          );
        }
        if (roadmapStatus === 'failed') {
          return (
            <section id="roadmap" style={{ marginTop: 36, scrollMarginTop: 20 }}>
              <SectionHeading title={heading} />
              <p style={{ color: '#64748b', margin: 0 }}>
                We couldn't generate a roadmap for you right now. Reply to this invite and we'll put something together manually.
              </p>
            </section>
          );
        }
        return null;
      })()}

      {/* R13 Step 8 (tasks 2 + 3) -- section order is now:
          Overview -> Roadmap -> What we can help with -> Research ->
          Feedback form -> footer. Old sections.services / .ai / .grow
          / .resources CardGrid blocks are REMOVED -- "What we can help
          with" is the single catalog view (from connect_offerings +
          status wording + open pilot programmes). */}
      <WhatWeCanHelpWithSection fb={feedback} />

      {(insights.length > 0 || research.length > 0) && (
        <section id="research" style={{ marginTop: 36, scrollMarginTop: 20 }}>
          {insights.length > 0 && (
            <>
              <SectionHeading title="Market insights" />
              <CardGrid kind="insight" items={insights} onClick={(id) => recordClick('insight', id)} />
            </>
          )}
          {research.length > 0 && (
            <div style={{ marginTop: insights.length > 0 ? 24 : 0 }}>
              <SectionHeading title="Research & domain knowledge" />
              <CardGrid kind="research" items={research} onClick={(id) => recordClick('research', id)} />
            </div>
          )}
        </section>
      )}

      <FeedbackFormSection fb={feedback} />

      {page.cta?.show && (
        <section style={{
          marginTop: 40,
          padding: 24,
          borderRadius: 12,
          // CTA band uses SkillDelivery cyan→blue gradient so it reads
          // as the platform's call-to-action, not the customer's brand.
          background: SKD_CTA_GRADIENT,
          color: 'white',
          textAlign: 'center'
        }}>
          <h3 style={{ margin: '0 0 8px', color: 'white', fontSize: 20 }}>Make this your workspace</h3>
          <p style={{ margin: '0 0 16px', opacity: 0.9, fontSize: 14 }}>
            Create a free account to explore everything above.
          </p>
          {isPreview ? (
            <span
              aria-disabled="true"
              title="Disabled in preview"
              style={{
                display: 'inline-block', padding: '12px 24px', borderRadius: 8,
                background: 'rgba(255,255,255,0.4)', color: 'white',
                fontWeight: 700, fontSize: 15, cursor: 'not-allowed'
              }}
            >
              Disabled in preview
            </span>
          ) : (
            <a
              href={`${APP_BASE}/register`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                try {
                  localStorage.setItem(PENDING_INVITE_KEY, JSON.stringify({
                    token, expiresAt: meta?.expires_at || null
                  }));
                } catch (_) {}
                recordClick('cta', null);
              }}
              style={{
                display: 'inline-block', padding: '12px 24px', borderRadius: 8,
                background: 'white', color: SKD_CYAN_DARK,
                fontWeight: 700, textDecoration: 'none', fontSize: 15
              }}
            >
              {page.cta?.label || 'Create your free account'}
            </a>
          )}
        </section>
      )}

      <footer style={{
        marginTop: 32, paddingTop: 16, borderTop: '1px solid #e2e8f0',
        color: '#64748b', fontSize: 13
      }}>
        {page.owner?.full_name && (
          <p style={{ margin: '0 0 6px' }}>
            Sent by <strong style={{ color: '#334155' }}>{page.owner.full_name}</strong> on SkillDelivery
          </p>
        )}
        {page.footer?.opt_out_line && <p style={{ margin: '0 0 6px' }}>{page.footer.opt_out_line}</p>}
        {(page.footer?.reply_to || page.footer?.unsubscribe_url || page.footer?.erase_url || isPreview) && (
          <p style={{ margin: 0 }}>
            {page.footer?.reply_to && (
              <>
                Reply to:{' '}
                <a href={`mailto:${page.footer.reply_to}`} style={{ color: SKD_CYAN_DARK }}>
                  {page.footer.reply_to}
                </a>
              </>
            )}
            {page.footer?.reply_to && (page.footer?.unsubscribe_url || isPreview) && (
              <span style={{ color: '#cbd5e1' }}>{'  ·  '}</span>
            )}
            {page.footer?.unsubscribe_url ? (
              <a
                href={page.footer.unsubscribe_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: SKD_CYAN_DARK }}
              >
                Unsubscribe
              </a>
            ) : isPreview ? (
              <span
                title="Disabled in preview"
                style={{ color: '#cbd5e1', cursor: 'not-allowed' }}
              >
                Unsubscribe (disabled in preview)
              </span>
            ) : null}
            {(page.footer?.unsubscribe_url || isPreview) && (page.footer?.erase_url || isPreview) && (
              <span style={{ color: '#cbd5e1' }}>{'  ·  '}</span>
            )}
            {page.footer?.erase_url ? (
              <a
                href={page.footer.erase_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: SKD_CYAN_DARK }}
              >
                Remove my details
              </a>
            ) : isPreview ? (
              <span
                title="Disabled in preview"
                style={{ color: '#cbd5e1', cursor: 'not-allowed' }}
              >
                Remove my details (disabled in preview)
              </span>
            ) : null}
          </p>
        )}
      </footer>

      <FloatingChatButton color={SKD_CYAN} />
    </PreviewShell>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────────

export function GradientBanner({ brandColor, organisation, firstName, personal, entityType, domain, industryLabel }) {
  return (
    <div style={{
      padding: 28,
      borderRadius: 12,
      background: `linear-gradient(135deg, ${brandColor}, ${_darken(brandColor, 0.25)})`,
      color: 'white'
    }}>
      <div style={{ fontSize: 13, fontWeight: 600, opacity: 0.85, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
        Prepared for {organisation}
      </div>
      <h1 style={{ margin: '6px 0 10px', color: 'white', fontSize: 24 }}>
        Hi {firstName || 'there'}, here is your team's starting point
      </h1>
      {personal && (
        <p style={{ margin: '12px 0 0', opacity: 0.95, fontSize: 15, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
          {personal}
        </p>
      )}
      {(() => {
        // R13 Step 8 (task 4) -- show the Focus industry when the owner
        // set one, else the sniffed domain label; never emit "General"
        // when we actually know the industry. If BOTH are unknown
        // (industry_label=null AND domain=general) we skip the pill
        // entirely rather than showing a vague "General" tag.
        const industryPill = industryLabel && industryLabel.trim()
          ? industryLabel.trim()
          : (domain && domain !== 'general' ? _prettyDomain(domain) : null);
        if (!entityType && !industryPill) return null;
        return (
          <div style={{ marginTop: 16, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {entityType && <Pill>{_prettyEntity(entityType)}</Pill>}
            {industryPill && <Pill>{industryPill}</Pill>}
          </div>
        );
      })()}
    </div>
  );
}

export function Pill({ children }) {
  return (
    <span style={{
      padding: '4px 12px', borderRadius: 999,
      background: 'rgba(255,255,255,0.2)', color: 'white',
      fontSize: 12, fontWeight: 600
    }}>
      {children}
    </span>
  );
}

export function OwnerCard({ owner, meetingLabel, shareUrl }) {
  const [copied, setCopied] = useState(false);
  if (!owner || !owner.full_name) return null;
  const first = owner.full_name.split(/\s+/)[0];
  const subtitle = [owner.title, owner.organisation].filter(Boolean).join(' · ');

  async function _share() {
    if (!shareUrl) return;
    try {
      if (typeof navigator !== 'undefined' && navigator.share) {
        await navigator.share({ title: 'SkillDelivery workspace preview', url: shareUrl });
        return;
      }
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (_) { /* best-effort */ }
  }

  return (
    <div style={{
      marginTop: 20, padding: 16, borderRadius: 12,
      background: 'white', border: '1px solid #e2e8f0',
      display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap'
    }}>
      <InitialsBadge initials={_initialsFromName(owner.full_name)} color="#334155" size={48} />
      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 15 }}>{owner.full_name}</div>
        {subtitle && (
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>{subtitle}</div>
        )}
        {meetingLabel && (
          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>{meetingLabel}</div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {owner.email && (
          <a
            href={`mailto:${owner.email}`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 14px', borderRadius: 8,
              background: '#f1f5f9', color: '#334155',
              textDecoration: 'none', fontWeight: 600, fontSize: 13
            }}
          >
            <Mail size={14} /> Message {first}
          </a>
        )}
        {shareUrl && (
          <button
            type="button"
            onClick={_share}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 14px', borderRadius: 8,
              background: 'white', color: '#334155',
              border: '1px solid #e2e8f0',
              cursor: 'pointer', fontWeight: 600, fontSize: 13
            }}
          >
            <Share2 size={14} /> {copied ? 'Link copied' : 'Share with a colleague'}
          </button>
        )}
      </div>
    </div>
  );
}

// Visual placeholder — Phase 1b wires actual chat. Hovering shows a
// tooltip; click is a no-op. Positioned fixed bottom-right.
export function FloatingChatButton({ color }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 50 }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {hover && (
        <div style={{
          position: 'absolute', bottom: 60, right: 0,
          background: '#0f172a', color: 'white', fontSize: 12,
          padding: '6px 10px', borderRadius: 6, whiteSpace: 'nowrap',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }}>
          Ask SkillBot — available after sign-up
        </div>
      )}
      <button
        type="button"
        aria-label="Ask SkillBot — available after sign-up"
        disabled
        style={{
          width: 52, height: 52, borderRadius: '50%',
          background: color, color: 'white',
          border: 'none', cursor: 'not-allowed',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 8px 20px rgba(15, 23, 42, 0.18)',
          opacity: 0.95
        }}
      >
        <MessageCircle size={22} />
      </button>
    </div>
  );
}

export function SectionHeading({ title }) {
  return <h2 style={{ fontSize: 18, color: '#0f172a', margin: '0 0 14px' }}>{title}</h2>;
}

// R13 Step 7 -- OfferingsBlock was folded into FeedbackBlock so the
// pilot button can sit INSIDE each offering card with shared Turnstile
// state. Keep this comment as a breadcrumb for the next refactor.

// ─── R13 Step 6 (task 1) -- feedback capture with Turnstile ────────────────
//
// Fires POST /api/public/invites/:token/feedback. Every POST carries the
// current Turnstile token; the widget auto-refreshes on expiry.
// Supports:
//   - Per-step reaction (useful / not quite) on each roadmap stage.
//   - Textarea pair (goals + improve).
//   - Four quick actions. `wrong_person` opens an inline name/email/phone
//     form so the submitter can hand us the right contact (M5).
//   - CTA chips from the DB (connect_ctas via /content.ctas).
//   - Pilot CTA chips for offerings with an OPEN programme (M2 §content).
//
// Server-side limiter stays active (10 per 15 min per IP, 3 per hour per
// invite token). CAPTCHA_FAILED surfaces a friendly "please tick the box"
// line and resets the widget so the next submit tries fresh.
// R13 Step 8 (tasks 2 + 3) -- feedback state is lifted into a hook so
// InviteWelcome can split the UI into two sections ("What we can help
// with" + the feedback form) with Research sitting between them. One
// Turnstile widget still backs both.
export function useInviteFeedback({ token, isPreview, content, ownerFirst, roadmap }) {
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [goals, setGoals] = useState('');
  const [improve, setImprove] = useState('');
  const [perStep, setPerStep] = useState({}); // index -> 'useful'|'not_quite'
  const [note, setNote] = useState('');       // last friendly line to echo
  // Turnstile token lives in state so a POST reads the latest value
  // after a refresh. The widget stays mounted so the Cloudflare retry
  // flow (challenge expired → re-arm) works end-to-end.
  const [tsToken, setTsToken] = useState(null);
  const [tsError, setTsError] = useState(false);
  const tsRef = useRef(null);
  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
  const captchaActive = !!siteKey && !isPreview;

  const w = (content && content.welcome) || null;
  const label = (path, fallback) => {
    if (!w) return fallback;
    const parts = path.split('.');
    let cur = w;
    for (const p of parts) { cur = cur && cur[p]; if (cur === undefined || cur === null) return fallback; }
    return cur || fallback;
  };
  const offerings = (content && content.offerings) || [];
  const dbCtas = (content && content.ctas) || [];

  const steps = (roadmap && (roadmap.steps || roadmap.display?.steps)) || [];

  const resetCaptcha = () => {
    setTsToken(null);
    try { tsRef.current && tsRef.current.reset(); } catch (_) { /* best-effort */ }
  };

  // Open "wrong person" inline collection form (M5: {name,email,phone}).
  const [wrongForm, setWrongForm] = useState(null); // null | {name,email,phone}
  const openWrongPerson = () => setWrongForm({ name: '', email: '', phone: '' });
  const cancelWrongPerson = () => setWrongForm(null);

  const post = async (payload) => {
    if (isPreview) return false;
    if (captchaActive && !tsToken) {
      setNote(label('captcha_hint', 'Please tick the "I am human" box below, then try again.'));
      return false;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/public/invites/${encodeURIComponent(token)}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...payload, turnstile_token: tsToken || '' })
      });
      if (res.status === 429) {
        setNote('Too many submissions right now, please try again shortly.');
        return false;
      }
      if (res.status === 400) {
        const body = await res.json().catch(() => ({}));
        if (body && body.code === 'CAPTCHA_FAILED') {
          setNote('Security check expired. Please tick the box below and try again.');
          resetCaptcha();
          return false;
        }
      }
      if (!res.ok) {
        setNote('Could not record that just now — try again.');
        resetCaptcha();
        return false;
      }
      // Cloudflare tokens are one-shot; refresh for the next submit.
      resetCaptcha();
      return true;
    } catch (_) {
      setNote('Could not record that just now — try again.');
      resetCaptcha();
      return false;
    } finally { setBusy(false); }
  };

  const submitStepReaction = async (idx, rating) => {
    const prev = perStep[idx];
    if (prev) return; // one tap per step
    setPerStep({ ...perStep, [idx]: rating === 1 ? 'useful' : 'not_quite' });
    const ok = await post({ type: 'step_reaction', step_index: idx, rating });
    // On failure we leave the chip locally picked; the owner can retry
    // by tapping the OTHER chip (reaction is advisory not persistent).
    if (!ok) {
      const copy = { ...perStep }; delete copy[idx]; setPerStep(copy);
    }
  };

  const submitQuickAction = async (type, extra) => {
    const text = (type === 'request_call' || type === 'send_more_details' || type === 'not_interested')
      ? (improve || goals || '')
      : '';
    const ok = await post({ type, text, extra: extra || {} });
    if (ok) {
      setSubmitted(true);
      setNote(label('thank_you', `Thanks — ${ownerFirst} will get back to you within 2 working days.`));
    }
  };

  const submitWrongPerson = async () => {
    const form = wrongForm || {};
    const correct = {};
    if (form.name && form.name.trim())   correct.name  = form.name.trim().slice(0, 120);
    if (form.email && form.email.trim()) correct.email = form.email.trim().toLowerCase().slice(0, 240);
    if (form.phone && form.phone.trim()) correct.phone = form.phone.trim().slice(0, 32);
    const ok = await post({
      type: 'wrong_person',
      text: '',
      extra: Object.keys(correct).length ? { correct_contact: correct } : {}
    });
    if (ok) {
      setWrongForm(null);
      setSubmitted(true);
      setNote(label('thank_you', `Thanks — ${ownerFirst} will get back to you within 2 working days.`));
    }
  };

  const submitTextareas = async () => {
    const goalsText = goals.trim().slice(0, 2000);
    const improveText = improve.trim().slice(0, 2000);
    if (!goalsText && !improveText) return;
    // Each textarea needs its own Turnstile token (one-shot). Chain the
    // two posts: wait for the first to come back + widget to re-arm,
    // then fire the second.
    let allOk = true;
    if (goalsText) {
      const ok = await post({ type: 'goals', text: goalsText });
      if (!ok) allOk = false;
    }
    if (allOk && improveText) {
      // Wait briefly for the widget to re-render a fresh token after
      // the first reset. The user will see the widget flash; if the new
      // token hasn't landed we show the captcha hint.
      await new Promise((r) => setTimeout(r, 400));
      const ok = await post({ type: 'improve', text: improveText });
      if (!ok) allOk = false;
    }
    if (allOk) {
      setSubmitted(true);
      setNote(label('thank_you', `Thanks — ${ownerFirst} will get back to you within 2 working days.`));
    }
  };

  return {
    // state
    submitted, busy, note, perStep, tsRef, tsError, siteKey, captchaActive,
    goals, improve, wrongForm, offerings, dbCtas, steps,
    content, ownerFirst, isPreview,
    // actions
    setGoals, setImprove, setWrongForm, setTsToken, setTsError, setNote,
    openWrongPerson, cancelWrongPerson,
    submitStepReaction, submitQuickAction, submitWrongPerson, submitTextareas,
    label
  };
}

// R13 Step 8 (task 2) -- "What we can help with" section: offerings
// (name + status pill + details + pilot question + pilot button in the
// card). Replaces the old sections.services / sections.ai /
// sections.grow / sections.resources CardGrid stack.
export function WhatWeCanHelpWithSection({ fb }) {
  const { offerings, submitted, isPreview, busy, submitQuickAction, content, label } = fb;
  if (submitted || !offerings.length) return null;
  return (
    <section id="help" style={{ marginTop: 36, scrollMarginTop: 20 }}>
      <SectionHeading title="What we can help with" />
      <div style={{ display: 'grid', gap: 10 }}>
        {offerings.map((o) => (
          <div key={o.id} style={{
            padding: 14, borderRadius: 10,
            background: '#f8fafc', border: '1px solid #e2e8f0'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
              <strong style={{ color: '#0f172a', fontSize: 15 }}>{o.name}</strong>
              {o.status_line && (
                <span style={{
                  fontSize: 11, padding: '2px 8px', borderRadius: 999,
                  background: o.status === 'live' ? '#dcfce7'
                            : o.status === 'coming_soon' ? '#fef3c7' : '#e0e7ff',
                  color:      o.status === 'live' ? '#166534'
                            : o.status === 'coming_soon' ? '#92400e' : '#3730a3',
                  whiteSpace: 'nowrap'
                }}>{o.status_line}</span>
              )}
            </div>
            {o.details && (
              <div style={{ color: '#475569', fontSize: 13, marginTop: 6, whiteSpace: 'pre-wrap' }}>
                {o.details}
              </div>
            )}
            {o.pilot_question_line && (
              <div style={{
                marginTop: 10, padding: '8px 10px', borderRadius: 8,
                background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1d4ed8', fontSize: 13
              }}>
                {o.pilot_question_line}
              </div>
            )}
            {o.programme && (
              <div style={{ marginTop: 10 }}>
                <QuickActionButton
                  onClick={() => submitQuickAction('pilot_interest', {
                    offering_id: o.id,
                    programme_id: o.programme.id
                  })}
                  disabled={isPreview || busy}
                  label={
                    (content && content.pilot_cta_label)
                    || o.programme.cta_text
                    || label('pilot_cta_fallback', `Join the pilot — ${o.name}`)
                  }
                  variant="brand"
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

// R13 Step 8 (task 2 + 4) -- feedback form section: reactions, two
// text boxes, Request a call / Send me details, privacy (locked) +
// Turnstile. Heading comes from the page text key
// `invite_welcome.feedback_heading` (editable in /admin/connect-catalog
// Page text tab).
export function FeedbackFormSection({ fb }) {
  const {
    submitted, busy, note, perStep, tsRef, tsError, captchaActive,
    goals, improve, wrongForm, dbCtas, steps,
    ownerFirst, isPreview,
    setGoals, setImprove, setWrongForm, setTsToken, setTsError, setNote,
    openWrongPerson, cancelWrongPerson,
    submitStepReaction, submitQuickAction, submitWrongPerson, submitTextareas,
    label
  } = fb;

  if (submitted) {
    return (
      <section style={{
        marginTop: 36, padding: 20, borderRadius: 12,
        background: '#f0f9ff', border: '1px solid #bae6fd', color: '#0369a1'
      }}>
        <strong>{note || label('thank_you', `Thanks — ${ownerFirst} will get back to you within 2 working days.`)}</strong>
      </section>
    );
  }

  return (
    <section style={{ marginTop: 36 }}>
      <SectionHeading title={label('feedback_heading', 'Was this helpful?')} />

      {steps.length > 0 && (
        <div style={{ marginBottom: 20, display: 'grid', gap: 8 }}>
          <div style={{ color: '#64748b', fontSize: 13 }}>React to any step:</div>
          <div style={{ display: 'grid', gap: 6 }}>
            {steps.map((s, i) => {
              const title = (s && (s.name || s.title || s.step || `Step ${i + 1}`)) || `Step ${i + 1}`;
              const picked = perStep[i];
              return (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 12px', borderRadius: 8,
                  background: '#f8fafc', border: '1px solid #e2e8f0'
                }}>
                  <span style={{ flex: 1, color: '#334155', fontSize: 14 }}>{title}</span>
                  <ReactionChip
                    label={label('reaction.useful', 'Useful')}
                    active={picked === 'useful'}
                    onClick={() => submitStepReaction(i, 1)}
                    disabled={!!picked || isPreview || busy}
                  />
                  <ReactionChip
                    label={label('reaction.not_quite', 'Not quite')}
                    active={picked === 'not_quite'}
                    onClick={() => submitStepReaction(i, -1)}
                    disabled={!!picked || isPreview || busy}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      <label style={{ display: 'block', marginBottom: 12 }}>
        <div style={{ color: '#334155', fontSize: 13, marginBottom: 6 }}>
          {label('challenges_prompt', "What's the one thing you're trying to get right in the next 60-90 days?")}
        </div>
        <textarea
          value={goals}
          onChange={(e) => setGoals(e.target.value)}
          maxLength={600}
          rows={2}
          disabled={isPreview}
          style={{
            width: '100%', padding: '10px 12px', borderRadius: 8,
            border: '1px solid #e2e8f0', fontSize: 14, resize: 'vertical',
            fontFamily: 'inherit'
          }}
        />
      </label>

      <label style={{ display: 'block', marginBottom: 12 }}>
        <div style={{ color: '#334155', fontSize: 13, marginBottom: 6 }}>
          {label('improve_prompt', 'What would make this more useful?')}
        </div>
        <textarea
          value={improve}
          onChange={(e) => setImprove(e.target.value)}
          maxLength={600}
          rows={2}
          disabled={isPreview}
          style={{
            width: '100%', padding: '10px 12px', borderRadius: 8,
            border: '1px solid #e2e8f0', fontSize: 14, resize: 'vertical',
            fontFamily: 'inherit'
          }}
        />
      </label>

      {(goals.trim() || improve.trim()) && !isPreview && (
        <button
          type="button"
          onClick={submitTextareas}
          disabled={busy}
          style={{
            marginBottom: 16, padding: '8px 16px', borderRadius: 8,
            background: '#0891b2', color: 'white', border: 'none',
            fontSize: 14, fontWeight: 600, cursor: busy ? 'not-allowed' : 'pointer'
          }}
        >
          Send
        </button>
      )}

      {dbCtas.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
          {dbCtas
            .filter((cta) => cta && cta.action && cta.action !== 'pilot_interest')
            .map((cta) => {
              const feedbackType =
                cta.action === 'request_call' ? 'request_call'
                : cta.action === 'send_details' ? 'send_more_details'
                : null;
              if (cta.action !== 'link' && !feedbackType) return null;
              const onClick = () => {
                if (cta.action === 'link' && cta.link_url) {
                  window.open(cta.link_url, '_blank', 'noopener,noreferrer');
                  return;
                }
                return submitQuickAction(feedbackType, {});
              };
              return (
                <QuickActionButton
                  key={cta.id}
                  onClick={onClick}
                  disabled={isPreview || busy}
                  label={cta.label}
                />
              );
            })}
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
        <QuickActionButton
          onClick={openWrongPerson}
          disabled={isPreview || busy}
          label={label('privacy.wrong_person', 'Not the right person')}
          variant="muted"
        />
        <QuickActionButton
          onClick={() => submitQuickAction('not_interested', {})}
          disabled={isPreview || busy}
          label={label('privacy.not_interested', 'Not interested')}
          variant="muted"
        />
      </div>

      {wrongForm && (
        <div style={{
          marginTop: 12, padding: 12, borderRadius: 10,
          background: '#fffbea', border: '1px solid #fde68a'
        }}>
          <div style={{ color: '#92400e', fontSize: 13, marginBottom: 8 }}>
            Who is the right person? Share any of these — the owner reads this.
          </div>
          <input placeholder="Name" value={wrongForm.name}
            onChange={(e) => setWrongForm({ ...wrongForm, name: e.target.value })}
            style={_wpInputStyle} maxLength={120} />
          <input placeholder="Email" value={wrongForm.email}
            onChange={(e) => setWrongForm({ ...wrongForm, email: e.target.value })}
            style={_wpInputStyle} maxLength={240} type="email" />
          <input placeholder="Phone (incl. country code)" value={wrongForm.phone}
            onChange={(e) => setWrongForm({ ...wrongForm, phone: e.target.value })}
            style={_wpInputStyle} maxLength={32} />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button type="button" onClick={submitWrongPerson} disabled={busy}
              style={{ padding: '6px 14px', borderRadius: 6, background: '#92400e', color: 'white', border: 'none', fontSize: 13, cursor: busy ? 'not-allowed' : 'pointer' }}>
              Send
            </button>
            <button type="button" onClick={cancelWrongPerson} disabled={busy}
              style={{ padding: '6px 14px', borderRadius: 6, background: 'white', color: '#92400e', border: '1px solid #92400e', fontSize: 13, cursor: busy ? 'not-allowed' : 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {captchaActive && (
        <div style={{ marginTop: 16 }}>
          <Turnstile
            ref={tsRef}
            onVerify={(t) => { setTsToken(t); setTsError(false); setNote(''); }}
            onExpire={() => { setTsToken(null); setNote('Security check expired — please tick the box again.'); }}
            onError={() => { setTsError(true); setNote('Could not load the security check. Please reload the page.'); }}
            size="normal"
            action="connect-feedback"
          />
          {tsError && (
            <div style={{ color: '#b45309', fontSize: 12 }}>
              If this persists, your browser may be blocking Cloudflare's challenge.
            </div>
          )}
        </div>
      )}

      {note && !submitted && (
        <div style={{ marginTop: 12, color: '#b45309', fontSize: 13 }}>{note}</div>
      )}

      <div style={{ color: '#64748b', fontSize: 12, marginTop: 16 }}>
        {label('closing_note', `Your note goes directly to ${ownerFirst}, who usually replies within 2 working days.`)}
      </div>
    </section>
  );
}

const _wpInputStyle = {
  display: 'block', width: '100%', marginBottom: 6,
  padding: '6px 10px', borderRadius: 6, border: '1px solid #fde68a',
  fontSize: 13, fontFamily: 'inherit'
};

export function ReactionChip({ label, active, onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '4px 10px', borderRadius: 999,
        background: active ? '#0891b2' : '#ffffff',
        color: active ? 'white' : '#0f172a',
        border: `1px solid ${active ? '#0891b2' : '#cbd5e1'}`,
        fontSize: 12, cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled && !active ? 0.6 : 1
      }}
    >
      {label}
    </button>
  );
}

export function QuickActionButton({ label, onClick, disabled, variant }) {
  const colors = variant === 'muted'
    ? { bg: '#f1f5f9', fg: '#475569', bd: '#e2e8f0' }
    : variant === 'brand'
      ? { bg: '#0891b2', fg: 'white', bd: '#0891b2' }
      : { bg: '#ffffff', fg: '#0f172a', bd: '#cbd5e1' };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '8px 14px', borderRadius: 8,
        background: colors.bg, color: colors.fg,
        border: `1px solid ${colors.bd}`,
        fontSize: 13, fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1
      }}
    >
      {label}
    </button>
  );
}

export function Loading() { return <p style={{ color: '#64748b' }}>Loading…</p>; }

export function NotFound() {
  return (
    <div style={{ padding: 32, background: 'white', borderRadius: 12 }}>
      <h2 style={{ marginTop: 0 }}>Invitation not found</h2>
      <p>The link may be mistyped or has been revoked. If you were expecting an invitation, please reply to the email you received.</p>
    </div>
  );
}

export function Expired({ owner }) {
  return (
    <div style={{ padding: 32, background: 'white', borderRadius: 12 }}>
      <h2 style={{ marginTop: 0 }}>This invitation has expired</h2>
      <p>Here's how to reach {owner?.full_name || 'the sender'} directly:</p>
      {owner && <OwnerCard owner={owner} />}
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────────

function _initialsFromName(name) {
  if (!name) return '?';
  const w = name.trim().split(/\s+/);
  if (!w.length) return '?';
  if (w.length === 1) return w[0].slice(0, 2).toUpperCase();
  return (w[0][0] + w[1][0]).toUpperCase();
}

function _darken(hex, amt) {
  try {
    const r = Math.max(0, Math.round(parseInt(hex.slice(1, 3), 16) * (1 - amt)));
    const g = Math.max(0, Math.round(parseInt(hex.slice(3, 5), 16) * (1 - amt)));
    const b = Math.max(0, Math.round(parseInt(hex.slice(5, 7), 16) * (1 - amt)));
    return '#' + [r, g, b].map((n) => n.toString(16).padStart(2, '0')).join('');
  } catch (_) {
    return hex;
  }
}

const ENTITY_LABEL = {
  company: 'Company',
  university_college: 'University / College',
  institute: 'Training Institute',
  individual: 'Individual'
};
function _prettyEntity(v) { return ENTITY_LABEL[v] || v; }

const DOMAIN_LABEL = {
  warehousing: 'Warehousing',
  iseries: 'iSeries',
  civil: 'Civil Construction',
  apparel: 'Apparel / Stitching',
  erpnext: 'ERPNext',
  general: 'General'
};
function _prettyDomain(v) { return DOMAIN_LABEL[v] || v; }
